import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { finalize } from 'rxjs/operators';
import { AiAnalystMetricsService } from 'src/app/services/aiAnalystMetrics.service';
import { AiAnalystSuccessMetricsSummary } from 'src/app/models/aiAnalystSuccessMetricsSummary.model';
import { currentMonthStart, formatMonthLabel, shiftMonth } from '../../ai-usage-by-user/ai-usage-by-user.util';
import { AnswerSplit, monthEndDate, splitAnswers } from './ai-analyst-success-metrics.util';

const DEFAULT_LOCALE = 'en';
const PERCENT = 100;

@Component({
  standalone: false,
  selector: 'app-ai-analyst-success-metrics',
  templateUrl: './ai-analyst-success-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./ai-analyst-success-metrics.component.scss']
})
export class AiAnalystSuccessMetricsComponent implements OnInit, OnDestroy {
  isLoading = false;
  summary: AiAnalystSuccessMetricsSummary | null = null;
  answerSplit: AnswerSplit = splitAnswers(0, 0);
  ratedCount = 0;
  thumbsUpPercent = 0;
  thumbsDownPercent = 0;
  month: string;
  readonly maxMonth: string;

  private loadSubscription?: Subscription;

  constructor(
    private metricsService: AiAnalystMetricsService,
    private translateService: TranslateService
  ) {
    this.maxMonth = currentMonthStart();
    this.month = this.maxMonth;
  }

  get monthLabel(): string {
    return formatMonthLabel(this.month, this.translateService.currentLang || DEFAULT_LOCALE);
  }

  get canStepForward(): boolean {
    return this.month < this.maxMonth;
  }

  ngOnInit(): void {
    this.load();
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
  }

  stepMonth(delta: number): void {
    const next = shiftMonth(this.month, delta);
    if (next > this.maxMonth) return;
    this.month = next;
    this.load();
  }

  load(): void {
    this.loadSubscription?.unsubscribe();
    this.isLoading = true;
    this.loadSubscription = this.metricsService.getSuccessMetricsSummary(this.month, monthEndDate(this.month))
      .pipe(finalize(() => this.isLoading = false))
      .subscribe(summary => this.applySummary(summary));
  }

  private applySummary(summary: AiAnalystSuccessMetricsSummary | null): void {
    this.summary = summary;
    this.answerSplit = splitAnswers(summary?.totalQueryCount ?? 0, summary?.reportsGeneratedCount ?? 0);
    const thumbsUp = summary?.thumbsUpCount ?? 0;
    this.ratedCount = thumbsUp + (summary?.thumbsDownCount ?? 0);
    this.thumbsUpPercent = this.ratedCount ? (thumbsUp / this.ratedCount) * PERCENT : 0;
    this.thumbsDownPercent = this.ratedCount ? PERCENT - this.thumbsUpPercent : 0;
  }
}
